import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getInventoryStatus } from '../../actions/inventory';
import { 
    Monitor, 
    Package, 
    AlertCircle, 
    CheckCircle, 
    XCircle, 
    Download, 
    Search,
    BarChart3,
    TrendingUp,
    Database
} from 'lucide-react';
import * as XLSX from "xlsx";

const Status = ({
    loading,
    stockData,
    uniqueBrands,
    getInventoryStatus
}) => {

    const [stockType, setStockType] = useState(["Alloted", "Empty", "In-stock", "Disabled"]);
    const [stockDataMaped, setStockDataMaped] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);

    // First useEffect to fetch data only on component mount
useEffect(() => {
        document.title = 'Inventory Status';
        getInventoryStatus();
    }, []);

    // Second useEffect to process data when stockData changes
    useEffect(() => {
        if (stockData.length > 0) {
            let data = [];
            let totalData = {
                _id: "",
                vendingMachineId: "",
                vendingMachineName: "Total",
                totalContainers: "",
                totalAssignedContainers: "",
                brands:{}
            };
            stockData.forEach(stock => {
                const brandsFound = Object.keys(stock.brands).sort();                
                let brands = {};
                uniqueBrands.forEach(brand => {
                    if(totalData.brands[brand] === undefined){
                        totalData.brands[brand] = {};
                        totalData.brands[brand]["assignedContainers"] = 0;
                        totalData.brands[brand]["empty"] = 0;
                        totalData.brands[brand]["inStock"] = 0;
                        totalData.brands[brand]["disabled"] = 0;
                    }
                    if(brandsFound.includes(brand)){
                        brands[brand] = stock.brands[brand];
                        totalData.brands[brand]["assignedContainers"] += stock.brands[brand].assignedContainers;
                        totalData.brands[brand]["empty"] += stock.brands[brand].empty;
                        totalData.brands[brand]["inStock"] += stock.brands[brand].inStock;
                        totalData.brands[brand]["disabled"] += stock.brands[brand].disabled;
                    }else{
                        brands[brand] = { assignedContainers: 0, empty: 0, inStock: 0, disabled: 0 }
                    }
                });
                data.push(
                    {
                        _id: stock._id,
                        vendingMachineId: stock.vendingMachineId,
                        vendingMachineName: stock.vendingMachineName,
                        totalContainers: stock.totalContainers,
                        totalAssignedContainers: stock.totalAssignedContainers,
                        brands:brands
                    }
                )                
            });
            data.push({_id:"", vendingMachineId:"", vendingMachineName: "", totalContainers:"",
                totalAssignedContainers:"", brands:{}})
            data.push(totalData);
            setStockDataMaped(data);
            setLastUpdated(new Date());
        }
    }, [stockData, uniqueBrands]);

    // Filter data based on search term
    useEffect(() => {
        if (stockDataMaped.length > 0) {
            const filtered = stockDataMaped.filter(item => 
                item.vendingMachineName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.vendingMachineId?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredData(filtered);
        }
    }, [stockDataMaped, searchTerm]);

    const exportToExcel = ()  => {
        let fileName = "Inventory_Status_" + new Date().toJSON().slice(0,10).replace(/-/g,'_');
        var elt = document.getElementById('inventoryTable');
        var wb = XLSX.utils.table_to_book(elt, { sheet: "Inventory Status" });
        return XLSX.writeFile(wb, fileName  + '.xlsx');
    }

    // Calculate statistics
    const statistics = useMemo(() => {
        if (!stockDataMaped.length) return null;
        
        const totalRow = stockDataMaped[stockDataMaped.length - 1];
        if (!totalRow || !totalRow.brands) return null;

        let totalInStock = 0;
        let totalEmpty = 0;
        let totalDisabled = 0;
        let totalAssigned = 0;

        Object.values(totalRow.brands).forEach(brand => {
            totalInStock += brand.inStock || 0;
            totalEmpty += brand.empty || 0;
            totalDisabled += brand.disabled || 0;
            totalAssigned += brand.assignedContainers || 0;
        });

        return {
            totalMachines: stockData.length,
            totalBrands: uniqueBrands.length,
            totalInStock,
            totalEmpty,
            totalDisabled,
            totalAssigned,
            utilizationRate: totalAssigned > 0 ? ((totalInStock / totalAssigned) * 100).toFixed(1) : 0
        };
    }, [stockDataMaped, stockData, uniqueBrands]);

    const getStatusColor = (type) => {
        switch (type.toLowerCase()) {
            case 'alloted': return 'primary';
            case 'in-stock': return 'success';
            case 'empty': return 'warning';
            case 'disabled': return 'danger';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (type) => {
        switch (type.toLowerCase()) {
            case 'alloted': return <Package size={16} />;
            case 'in-stock': return <CheckCircle size={16} />;
            case 'empty': return <AlertCircle size={16} />;
            case 'disabled': return <XCircle size={16} />;
            default: return <Database size={16} />;
        }
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <BarChart3 size={32} className="me-3 text-primary" />
                                    Inventory Status
                                </h1>
                                <p className="text-muted mb-0">
                                    Real-time inventory tracking across all vending machines
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <button 
                                    onClick={() => getInventoryStatus()}
                                    className="btn btn-outline-primary btn-lg shadow-sm d-flex align-items-center"
                                    disabled={loading}
                                >
                                    <Database size={20} className="me-2" />
                                    {loading ? 'Refreshing...' : 'Refresh Data'}
                                </button>
                                <button 
                                    onClick={exportToExcel}
                                    className="btn btn-success btn-lg shadow-sm d-flex align-items-center"
                                    disabled={!stockDataMaped.length}
                                >
                                    <Download size={20} className="me-2" />
                                    Export to Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                {statistics && (
                    <div className="row mb-4">
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-primary text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalMachines}</div>
                                            <div className="small">Machines</div>
                                        </div>
                                        <Monitor size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-info text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalBrands}</div>
                                            <div className="small">Brands</div>
                                        </div>
                                        <Package size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-success text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalInStock}</div>
                                            <div className="small">In Stock</div>
                                        </div>
                                        <CheckCircle size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-warning text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalEmpty}</div>
                                            <div className="small">Empty</div>
                                        </div>
                                        <AlertCircle size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-danger text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalDisabled}</div>
                                            <div className="small">Disabled</div>
                                        </div>
                                        <XCircle size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-4 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-secondary text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.utilizationRate}%</div>
                                            <div className="small">Utilization</div>
                                        </div>
                                        <TrendingUp size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by machine name or ID..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-1">
                                        <Database size={20} className="text-muted me-2" />
                                        <span className="text-muted">
                                            Showing {filteredData.length > 2 ? filteredData.length - 2 : 0} machines
                                            {searchTerm && ` matching "${searchTerm}"`}
                                        </span>
                                    </div>
                                    {lastUpdated && (
                                        <div className="small text-muted">
                                            Last updated: {lastUpdated.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Inventory Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-white border-bottom">
                        <h5 className="card-title mb-0 d-flex align-items-center">
                            <Monitor size={20} className="me-2 text-primary" />
                            Inventory Details
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading inventory data...</p>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <Monitor size={64} className="text-muted" />
                                </div>
                                <h4 className="text-muted mb-3">
                                    {searchTerm ? 'No machines found' : 'No inventory data available'}
                                </h4>
                                <p className="text-muted mb-4">
                                    {searchTerm 
                                        ? `No machines match your search for "${searchTerm}". Try adjusting your search terms.`
                                        : 'No inventory data has been loaded yet. Click refresh to load the latest data.'
                                    }
                                </p>
                                {searchTerm ? (
                                    <button 
                                        className="btn btn-outline-primary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Clear Search
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => getInventoryStatus()}
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        <Database size={20} className="me-2" />
                                        Load Inventory Data
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table 
                                    id="inventoryTable" 
                                    className="table table-hover mb-0"
                                    style={{ minWidth: '1200px' }}
                                >
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th rowSpan="2" className="text-center align-middle border-end fw-bold">
                                                <Monitor size={16} className="me-1" />
                                                Machine
                                            </th>
                                            <th rowSpan="2" className="text-center align-middle border-end fw-bold">
                                                ID
                                            </th>
                                            <th rowSpan="2" className="text-center align-middle border-end fw-bold">
                                                Total<br/>Containers
                                            </th>
                                            <th rowSpan="2" className="text-center align-middle border-end fw-bold">
                                                Total<br/>Allocated
                                            </th>
                                            {uniqueBrands.map((brand, index) => (
                                                <th 
                                                    key={index} 
                                                    colSpan="4" 
                                                    className="text-center border-end fw-bold bg-light"
                                                >
                                                    <Package size={16} className="me-1" />
                                                    {brand}
                                                </th>
                                            ))}
                                        </tr>
                                        <tr>
                                            {uniqueBrands.map((brand, brandIndex) => 
                                                stockType.map((type, typeIndex) => (
                                                    <th 
                                                        key={`${brandIndex}-${typeIndex}`} 
                                                        className={`text-center text-${getStatusColor(type)} fw-bold`}
                                                        style={{ fontSize: '0.85rem' }}
                                                    >
                                                        {getStatusIcon(type)}
                                                        <span className="ms-1">{type}</span>
                                                    </th>
                                                ))
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((vendingMachineData, index) => {
                                            const isEmptyRow = !vendingMachineData.vendingMachineName && !vendingMachineData.vendingMachineId;
                                            const isTotalRow = vendingMachineData.vendingMachineName === "Total";
                                            
                                            if (isEmptyRow) {
                                                return (
                                                    <tr key={index} className="border-top-3">
                                                        <td colSpan={4 + (uniqueBrands.length * 4)} className="p-1"></td>
                                                    </tr>
                                                );
                                            }

                                            return (
                                                <tr 
                                                    key={index} 
                                                    className={isTotalRow ? 'table-primary fw-bold' : 'table-row-hover'}
                                                >
                                                    <td className={`${isTotalRow ? 'fw-bold' : ''} border-end`}>
                                                        {isTotalRow ? (
                                                            <span className="d-flex align-items-center">
                                                                <TrendingUp size={16} className="me-2 text-primary" />
                                                                {vendingMachineData.vendingMachineName}
                                                            </span>
                                                        ) : (
                                                            vendingMachineData.vendingMachineName
                                                        )}
                                                    </td>
                                                    <td className={`${isTotalRow ? 'fw-bold' : ''} border-end`}>
                                                        {vendingMachineData.vendingMachineId}
                                                    </td>
                                                    <td className={`text-center ${isTotalRow ? 'fw-bold' : ''} border-end`}>
                                                        {vendingMachineData.totalContainers}
                                                    </td>
                                                    <td className={`text-center ${isTotalRow ? 'fw-bold' : ''} border-end`}>
                                                        {vendingMachineData.totalAssignedContainers}
                                                    </td>
                                                    {Object.keys(vendingMachineData.brands).map((brand, brandIndex) => 
                                                        Object.keys(vendingMachineData.brands[brand]).map((attribute, attrIndex) => {
                                                            const value = vendingMachineData.brands[brand][attribute];
                                                            const statusType = attribute === 'assignedContainers' ? 'alloted' : 
                                                                             attribute === 'inStock' ? 'in-stock' : 
                                                                             attribute === 'empty' ? 'empty' : 'disabled';
                                                            
                                                            return (
                                                                <td 
                                                                    key={`${brandIndex}-${attrIndex}`} 
                                                                    className={`text-center ${isTotalRow ? 'fw-bold' : ''}`}
                                                                >
                                                                                                                        <span 
                                                        className={`badge bg-${getStatusColor(statusType)} bg-opacity-10 text-${getStatusColor(statusType)} border border-${getStatusColor(statusType)} border-opacity-25`}
                                                        style={{ 
                                                            minWidth: '40px',
                                                            fontWeight: isTotalRow ? 'bold' : 'normal',
                                                            fontSize: isTotalRow ? '0.9rem' : '0.8rem'
                                                        }}
                                                    >
                                                        {value}
                                                    </span>
                                                                </td>
                                                            );
                                                        })
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Info */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card shadow-sm bg-light">
                            <div className="card-body">
                                <div className="row text-center">
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span className={`badge bg-primary me-2`}>
                                                {getStatusIcon('alloted')}
                                            </span>
                                            <span className="small text-muted">Allocated Containers</span>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span className={`badge bg-success me-2`}>
                                                {getStatusIcon('in-stock')}
                                            </span>
                                            <span className="small text-muted">Available Stock</span>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span className={`badge bg-warning me-2`}>
                                                {getStatusIcon('empty')}
                                            </span>
                                            <span className="small text-muted">Empty Containers</span>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span className={`badge bg-danger me-2`}>
                                                {getStatusIcon('disabled')}
                                            </span>
                                            <span className="small text-muted">Disabled Slots</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

Status.propTypes = {
    getInventoryStatus: PropTypes.func,
    stockData: PropTypes.array,
    uniqueBrands: PropTypes.array
}

const mapStateToProps = state => ({
    stockData: state.inventory.stockData,
    uniqueBrands: state.inventory.uniqueBrands,
    loading: state.inventory.loading,
});

export default connect(mapStateToProps, { getInventoryStatus } )(Status);